/**
 * God mode — client half.
 *
 * Signs the app's ONE Firebase app into a token minted for another user, so
 * every page, query, listener and callable behaves exactly as it does for them.
 *
 * WHY THE SINGLE SESSION, and not a second Firebase app with its own Firestore
 * handle: 39 files import `db` from config/firebase directly. Routing them
 * through an injected handle would be 39 places to get right, and every page
 * added afterwards would be a 40th chance to forget — a page still reading as
 * the superadmin while the banner says you are someone else is precisely the
 * silent divergence this feature exists to catch. Swapping the session means
 * there is nothing to remember: correctness is the default, including for code
 * nobody has written yet.
 *
 * THE COST, handled here. Signing in as the target REPLACES the superadmin's
 * persisted session, so `beginGodmode` hands back a restore token with the
 * view token. It lives in sessionStorage — tab-scoped, gone when the tab
 * closes — and is exchanged on exit. It is strictly less powerful than the
 * refresh token the browser already held for that account, and expires in an
 * hour.
 *
 * Losing it must never leave a superadmin quietly operating as someone else,
 * so `recoverOrphanedSession` runs at startup: a god-mode session with no
 * restore token is signed out, not continued. Fail closed.
 *
 * WRITES. A god-mode session may change things — that is what it is for —
 * as the target, with the target's permissions, and only on the record. None
 * of that is enforced here: firestore.rules (godmodeAudited() on every write)
 * requires an audit entry in the same batch as the write, which
 * src/lib/firestoreAudited.js adds underneath every page via a Vite alias;
 * storage.rules require one written beforehand (src/lib/storageAudited.js);
 * mutating callables record themselves server-side (auditGodmodeCall). What
 * this module does is open and close the session document those entries hang
 * off (beginGodmode / endGodmode) and make the state visible in the banner.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

const RESTORE_KEY = 'godmode.restoreToken';
const TARGET_KEY = 'godmode.target';
const STARTED_KEY = 'godmode.startedAt';
const MAX_KEY = 'godmode.maxMs';

/** sessionStorage can throw (private mode, blocked storage). Never fatal. */
const store = {
  get(key) {
    try { return window.sessionStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.sessionStorage.setItem(key, value); return true; } catch { return false; }
  },
  clear() {
    try {
      [RESTORE_KEY, TARGET_KEY, STARTED_KEY, MAX_KEY]
        .forEach((k) => window.sessionStorage.removeItem(k));
    } catch { /* nothing to clear */ }
  },
};

/** The `godmode` claim on the CURRENT session, straight from the ID token. */
export async function readGodmodeClaims() {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const { claims } = await user.getIdTokenResult();
    if (claims?.godmode !== true) return null;
    return {
      actorUid: claims.godmode_actor || null,
      startedAt: Number(claims.godmode_started) || null,
      auditId: claims.godmode_audit || null,
      // Names the godmode_sessions/{id} document the write audit hangs off.
      // Absent on tokens minted before write auditing: those stay read-only.
      sessionId: claims.godmode_session || null,
    };
  } catch (err) {
    console.error('[godmode] could not read ID token claims:', err);
    return null;
  }
}

/** The target we stashed when the session started, for the banner after a reload. */
export function readStoredTarget() {
  const raw = store.get(TARGET_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function sessionDeadline() {
  const startedAt = Number(store.get(STARTED_KEY));
  const maxMs = Number(store.get(MAX_KEY));
  if (!startedAt || !maxMs) return null;
  return startedAt + maxMs;
}

/** Staff the signed-in superadmin may view as. */
export async function listTargets() {
  const fn = httpsCallable(getFunctions(), 'listGodmodeTargets');
  const { data } = await fn();
  return data;
}

/**
 * Start viewing as `targetUid`.
 *
 * Order matters. The restore token is stashed BEFORE the session is swapped:
 * stash-after would leave a window where a crash strands the superadmin in a
 * session they cannot get out of. If the stash fails (storage blocked), the
 * swap does not happen at all — being unable to leave is worse than being
 * unable to enter.
 */
export async function enterGodMode(targetUid) {
  const fn = httpsCallable(getFunctions(), 'beginGodmode');
  const { data } = await fn({ targetUid });

  const stashed = store.set(RESTORE_KEY, data.restoreToken)
    && store.set(TARGET_KEY, JSON.stringify(data.target))
    && store.set(STARTED_KEY, String(data.startedAt))
    && store.set(MAX_KEY, String(data.sessionMaxMs));

  if (!stashed) {
    store.clear();
    throw new Error(
      'God mode needs session storage to keep the way back to your own account, '
      + 'and this browser refused it. Nothing was changed.',
    );
  }

  await signInWithCustomToken(auth, data.viewToken);
  return data.target;
}

/**
 * Close the session document, while still signed in as the target (the
 * token is what names the session). Best-effort: a failure here must never
 * stop the exit, and an unclosed session simply expires. The audit page shows
 * a real duration when this succeeds.
 */
async function endSessionRecord(reason) {
  try {
    const claims = await readGodmodeClaims();
    if (!claims?.sessionId) return;
    const fn = httpsCallable(getFunctions(), 'endGodmode');
    await fn({ reason });
  } catch (err) {
    console.warn('[godmode] could not close the session record:', err?.message || err);
  }
}

/**
 * Return to your own account.
 *
 * A failed restore is NOT left as "still impersonating" — the session is
 * signed out, which costs a login and never leaves the wrong identity active.
 */
export async function exitGodMode(reason = 'exit') {
  await endSessionRecord(reason);
  const restoreToken = store.get(RESTORE_KEY);
  store.clear();

  if (!restoreToken) {
    await signOut(auth);
    return { restored: false, reason: 'no-restore-token' };
  }

  try {
    await signInWithCustomToken(auth, restoreToken);
    return { restored: true };
  } catch (err) {
    console.error('[godmode] restore failed, signing out:', err);
    await signOut(auth);
    return { restored: false, reason: 'restore-failed' };
  }
}

/**
 * Called once at startup, before anything renders.
 *
 * Catches the states a reload can land in:
 *   - a god-mode session whose restore token is gone (tab reopened, storage
 *     cleared): sign out. Never continue as someone else by accident.
 *   - a god-mode session past its deadline: exit.
 *   - a stale restore token with no god-mode session: just clear it.
 *
 * Returns what it did, so the UI can say why it signed you out.
 */
export async function recoverOrphanedSession() {
  const claims = await readGodmodeClaims();

  if (!claims) {
    if (store.get(RESTORE_KEY)) store.clear();
    return { action: 'none' };
  }

  if (!store.get(RESTORE_KEY)) {
    await signOut(auth);
    return { action: 'signed-out', reason: 'orphaned' };
  }

  const deadline = sessionDeadline();
  if (deadline && Date.now() > deadline) {
    const { restored } = await exitGodMode('expired');
    return { action: restored ? 'restored' : 'signed-out', reason: 'expired' };
  }

  return { action: 'resumed', target: readStoredTarget(), claims };
}
